import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { OnboardingV2State } from '../../../store/onboardingV2/types.ts';
import { Question } from '../../../hooks/onboardingV2/useConversationalOnboarding.ts';
import { OnboardingStep } from '../../../utils/onboardingV2/conversationalOnboardingSteps.ts';
import AccountTypeSelector from './AccountTypeSelector/AccountTypeSelector.tsx';
import SkillsSelector from './SkillsSelector/SkillsSelector.tsx';
import ProfilesSelector from './ProfilesSelector/ProfilesSelector.tsx';
import WorkZonesSelector from './WorkZonesSelector/WorkZonesSelector.tsx';
import SummaryScreen from './SummaryScreen/SummaryScreen.tsx';
import CompleteActions from './CompleteActions/CompleteActions.tsx';
import CommerceOffer from './CommerceOnboarding/CommerceOffer.tsx';
import CommerceForm from './CommerceOnboarding/CommerceForm.tsx';
import commerceFormStyles from './CommerceOnboarding/CommerceForm.module.scss';
import { buildCommercePrefillFromOnboardingState } from '../../../utils/commercePrefill.ts';

interface InteractiveComponentsProps {
  question: Question;
  state: OnboardingV2State;
  onAction: (action: string, data?: any) => void;
  setSelectedCategoryFilter?: (categoryId: number | null) => void;
  onNavigateToStep?: (step: OnboardingStep) => void;
  isProcessing?: boolean;
}

const InteractiveComponents: React.FC<InteractiveComponentsProps> = ({
  question,
  state,
  onAction,
  setSelectedCategoryFilter,
  onNavigateToStep,
  isProcessing = false,
}) => {
  const userId = useSelector((s: { general?: { userData?: { id?: number } } }) => s.general?.userData?.id ?? 0);
  const commercePrefill = useMemo(() => buildCommercePrefillFromOnboardingState(state), [state]);

  if (question.interactiveType === 'accountType') {
    return <AccountTypeSelector state={state} onAction={onAction} />;
  }

  if (question.interactiveType === 'skills') {
    return (
      <SkillsSelector
        state={state}
        onAction={onAction}
        setSelectedCategoryFilter={setSelectedCategoryFilter}
      />
    );
  }

  if (question.interactiveType === 'profiles') {
    return <ProfilesSelector state={state} onAction={onAction} />;
  }

  if (question.interactiveType === 'summary') {
    return <SummaryScreen state={state} onAction={onAction} />;
  }

  if (question.interactiveType === 'workZones') {
    return <WorkZonesSelector state={state} onAction={onAction} />;
  }

  if (question.interactiveType === 'completeActions') {
    return (
      <CompleteActions
        state={state}
        onAction={onAction}
        onNavigateToStep={onNavigateToStep}
      />
    );
  }

  if (question.interactiveType === 'commerceOffer') {
    return <CommerceOffer onAction={onAction} />;
  }

  if (question.interactiveType === 'commerceForm') {
    if (!userId) {
      return (
        <div className={commerceFormStyles.form}>
          <p className={commerceFormStyles.hint}>No pudimos cargar tu usuario. Podés continuar al inicio.</p>
          <div className={commerceFormStyles.actions}>
            <button type="button" className={commerceFormStyles.cancel} onClick={() => onAction('commerceOnboardingSkip')}>
              Ir al inicio
            </button>
          </div>
        </div>
      );
    }
    return (
      <CommerceForm
        prefill={commercePrefill}
        userId={userId}
        disabled={isProcessing}
        variant="embedded"
        onSkip={() => onAction('commerceOnboardingSkip')}
        onSubmit={(payload) => onAction('commerceOnboardingSubmit', payload)}
      />
    );
  }

  return null;
};

export default InteractiveComponents;
